// @ts-nocheck
import React, { useState, useEffect } from "react";
import { AssetBreadCrumb } from "../AssetBreadCrumb";
import AssetDescription from "../AssetDescription";
import ExploreDataSourceAdd from "./ExploreDataSourceAdd";
import ExploreDataSourceForm from "./ExploreDataSourceForm";
import ExploreDataSourceList from "./ExploreDataSourceList";
import usePrevious from "../../hooks/usePrevious";
import { v4 as uuidv4 } from "uuid";
import {
  dataSourceAdapters,
  fetchRecordDataSource,
  hasDataChanged,
  setSensitivityModelRenderProperties,
} from "./utils";
import ExploreGraph from "./ExploreGraph";
import { blankDataSource } from "./dataSourceSchemas";
import { SELECTION_STEPS } from "./ButtonSelectionDataSourceForm";
import { PageContainer } from "../AssetBreadCrumb";
// import dataSourceExample from './2024-11-04-08-dataSourceExample.json'

const displays = Object.freeze({
  graph: "graph",
  addDataSource: "addDataSource",
  graphWForm: "graphWForm",
  graphWDashBoard: "graphWDashBoard"
});

const networkStatus = Object.freeze({
  unfetched: "unfetched",
  loading: "loading",
  loaded: "loaded",
});

const ExploreDashboard = ({ selectedAsset, assetOwner, assetGroup }) => {
  const [displayProperties, setDisplayProperties] = useState({
    view: displays.graph,
    activeDataSource: null,
    isEditing: false
  });
  const [dataSourceList, setDataSourceList] = useState({});
  const [pendingDataSourceId, setPendingDataSourceId] = useState(null);
  const previousDataSourceList = usePrevious(dataSourceList);
  

  // Need this for later
  useEffect(() => {
    const fetchChangedDataSources = async () => {
      const changedKeys = hasDataChanged(
        previousDataSourceList,
        dataSourceList
      );

      if (changedKeys.length > 0) {
        const promises = changedKeys.map(async (dataSourceId) => {
          const record = dataSourceList[dataSourceId];
          try {
            const updatedRecord = await fetchRecordDataSource(
              dataSourceId,
              record
            );

            setDataSourceList((currentList) => ({
              ...currentList,
              [dataSourceId]: updatedRecord,
            }));
          } catch (e) {
            console.log("Error, ", e);

            setDataSourceList((currentList) => ({
              ...currentList,
              [dataSourceId]: { ...record, status: "error" },
            }));
          }
        });
        await Promise.allSettled(promises);
      }
    };
    fetchChangedDataSources();
  }, [dataSourceList, previousDataSourceList]);

  const handleSubmit = () => {
    // Clear the pending data source ID when saving
    setPendingDataSourceId(null);
    setDisplayProperties({
      view: displays.graphWDashBoard,
      activeDataSource: null,
      isEditing: false
    });
  };
  
  const handleCancel = () => {
    // If we have a pending data source, remove it from the list
    if (pendingDataSourceId) {
      setDataSourceList((prev) => {
        const updatedDSList = { ...prev };
        delete updatedDSList[pendingDataSourceId];
        return updatedDSList;
      });
      setPendingDataSourceId(null);
    }
    
    setDisplayProperties({
      view: displays.graph,
      activeDataSource: null,
      isEditing: false
    });
  };

  const handleDataSourceChange = async (updatedDataSourcRecord) => {
    const [updatedDataSourcRecordId] = Object.keys(updatedDataSourcRecord)
    const record = updatedDataSourcRecord[updatedDataSourcRecordId]
    setDataSourceList((currentList) => ({
      ...currentList,
      [updatedDataSourcRecordId]: record,
    }));
  };

  const renderContent = () => { 
    const formattedData = dataSourceAdapters(dataSourceList);
    switch (displayProperties.view) {
      case displays.graph:
        return (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 flex-1" style={{ height: Object.keys(dataSourceList).length > 0 ? "600px" : "auto", width: "100%" }}>
              {Object.keys(dataSourceList).length > 0 ? (
                <div className="w-full h-full">
                  <ExploreGraph data={formattedData} />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-lg font-medium">No data sources added yet</p>
                  <p className="mt-1 text-sm">Add a data source from the sidebar to start your analysis</p>
                  <button
                    onClick={handleAddNewDataSource}
                    className="mt-6 px-4 py-2 bg-eai-primary-accent text-white font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add Data Source
                  </button>
                </div>
              )}
            </div>
          </div>
        );
        
      case "addDataSource":
        return (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-gray-900 text-lg font-semibold font-Inter">Configure Data Source</h2>
            </div>
            <div className="p-4">
              <ExploreDataSourceForm
                selectedAsset={selectedAsset}
                onDataSourceChange={handleDataSourceChange}
                dataSourceRecord={{
                  [displayProperties.activeDataSource]:
                    dataSourceList[displayProperties.activeDataSource],
                }}
                onSubmitForm={handleSubmit}
                onCancel={handleCancel}
                initialStep={displayProperties.isEditing ? SELECTION_STEPS.STYLE : undefined}
              />
            </div>
          </div>
        );
        
      case displays.graphWForm:
        return (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-gray-900 text-lg font-semibold font-Inter">Analysis Graph & Configuration</h2>
            </div>
            <div className="p-4 flex flex-col gap-5">
              <div className="bg-gray-50 rounded-md border border-gray-200">
                <div className="p-4 border-b border-gray-200 bg-white rounded-t-md">
                  <h3 className="text-gray-900 text-md font-medium font-Inter">Analysis Graph</h3>
                </div>
                <div className="p-4 bg-white rounded-b-md" style={{ height: "400px" }}>
                  <ExploreGraph data={formattedData} />
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-md border border-gray-200">
                <div className="p-4 border-b border-gray-200 bg-white rounded-t-md">
                  <h3 className="text-gray-900 text-md font-medium font-Inter">Configure Data Source</h3>
                </div>
                <div className="p-4 bg-white rounded-b-md">
                  <ExploreDataSourceForm
                    selectedAsset={selectedAsset}
                    onDataSourceChange={handleDataSourceChange}
                    dataSourceRecord={{
                      [displayProperties.activeDataSource]:
                        dataSourceList[displayProperties.activeDataSource],
                    }}
                    onSubmitForm={handleSubmit}
                    onCancel={handleCancel}
                    initialStep={displayProperties.isEditing ? SELECTION_STEPS.STYLE : undefined}
                  />
                </div>
              </div>
            </div>
          </div>
        );
        
      case displays.graphWDashBoard:
        return (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-gray-900 text-lg font-semibold font-Inter">Analysis Dashboard</h2>
            </div>
            <div className="p-4 flex flex-col gap-5">
              <div className="bg-gray-50 rounded-md border border-gray-200">
                <div className="p-4 border-b border-gray-200 bg-white rounded-t-md">
                  <h3 className="text-gray-900 text-md font-medium font-Inter">Analysis Graph</h3>
                </div>
                <div className="p-4 bg-white rounded-b-md" style={{ height: "400px" }}>
                  <ExploreGraph data={formattedData} />
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-md border border-gray-200">
                <div className="p-4 border-b border-gray-200 bg-white rounded-t-md">
                  <h3 className="text-gray-900 text-md font-medium font-Inter">Dashboard View</h3>
                </div>
                <div className="p-4 bg-white rounded-b-md">
                  <div className="text-center py-8 text-gray-500">
                    <p>Analysis dashboard will appear here</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex-1 overflow-y-auto">
            <div className="p-8 text-center text-gray-500">
              <p>No content available for this type.</p>
            </div>
          </div>
        );
    }
  };

  const handleDeleteDataSource = (dataSourceID) => {
    setDataSourceList((prev) => {
      const updatedDSList = { ...prev }; // Create a shallow copy
      delete updatedDSList[dataSourceID]; // Delete the key from the copy
      return updatedDSList; // Return the updated object as the new state
  });

  }

  const handleDataSourceRecordClick = (dataSourceID) => {
    setDisplayProperties({
      view: displays.graphWForm,
      activeDataSource: dataSourceID,
      isEditing: true
    });
  }

  const handleAddNewDataSource = () => {
    const id = uuidv4();
    setDataSourceList((currentList) => ({
      ...currentList,
      [id]: blankDataSource,
    }));
    
    // Set this as our pending data source
    setPendingDataSourceId(id);

    setDisplayProperties({
      view: displays.graphWForm,
      activeDataSource: id,
      isEditing: false
    });
  };

  return (
    <PageContainer fullHeight>
      <AssetBreadCrumb assetGroupName="New Analysis" />
      <div className="flex-1 min-h-0 bg-white rounded-md border border-gray-200 overflow-hidden flex">
        <div className="w-[300px] border-r border-gray-200 hidden xl:flex flex-col shrink-0">
        <div className="h-[93px] pl-6 py-[20px]">
          <div className="text-gray-900 text-xl font-semibold leading-[30px]">
            Data Sources
          </div>
          <div className="text-gray-400 text-sm font-normal">
            Add and configure data sources
          </div>
        </div>
        
        <div className="h-[65px] py-[12px] pl-6 pr-[22px] bg-gray-200">
          <div className="flex items-center justify-between">
            <button
              onClick={handleAddNewDataSource}
              className="px-3 py-2 bg-eai-primary-accent text-white rounded-md flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">Add Data</span>
            </button>
            
            <div className="flex items-center">
              <div className="text-gray-900 text-sm font-semibold mr-2">Sources</div>
              <div className="h-[19px] px-2 bg-slate-400 rounded-[100px] flex justify-center items-center">
                <div className="text-gray-50 text-xs font-medium font-['Inter'] leading-none">
                  {Object.keys(dataSourceList).filter(id => id !== pendingDataSourceId).length}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex-1">
          {/* Filter out the pending data source if it exists */}
          {Object.keys(dataSourceList).length > 0 && Object.keys(dataSourceList).some(id => id !== pendingDataSourceId) ? (
            <ExploreDataSourceList 
              dataSourceList={Object.entries(dataSourceList).reduce((acc, [id, source]) => {
                // Only include non-pending data sources
                if (id !== pendingDataSourceId) {
                  acc[id] = source;
                }
                return acc;
              }, {})}
              onDelete={handleDeleteDataSource} 
              onRecordClick={handleDataSourceRecordClick} 
            />
          ) : (
            <div className="p-5 text-center text-gray-400">
              <p>No data sources added yet</p>
            </div>
          )}
        </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="shrink-0">
            <div className="flex flex-row h-24 p-5 justify-left items-center">
              <div className="flex grow items-center">
                <div className="flex-col items-center justify-center">
                  <div className="text-gray-900 text-xl font-semibold font-Inter mb-1">
                    New Analysis
                  </div>
                  <div className="text-gray-500 text-sm font-normal font-Inter">
                    Compare and evaluate your forecasting models.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-row flex-1 min-h-0">
            {renderContent()}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ExploreDashboard;
